import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { COPY_BARCODE_PATTERN, generateCopyBarcode, sanitizeCopyBarcode } from '@/lib/barcode';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Plus, RefreshCw, Trash2 } from 'lucide-react';
import {
  useCreateBookCopy,
  useShelfLocations,
  useConditions,
  type CreateBookCopyData,
} from '@/hooks/inventory-hooks';
import Drawer from '@/components/admin/components/Drawer';

// Validation schema for adding new book copies
const addBookCopySchema = z.object({
  copies: z.array(z.object({
    barcode: z.string().regex(COPY_BARCODE_PATTERN, 'Barcode must be exactly 12 digits'),
    accession_number: z.string().min(5, 'Accession number must be at least 5 characters'),
    acquisition_date: z.string().min(1, 'Acquisition date is required'),
    acquisition_cost: z.string().optional(),
    acquisition_source: z.string().optional(),
    location_id: z.number().nullable().optional(),
    condition_id: z.number().min(1, 'Condition is required'),
    status: z.enum(['available', 'maintenance', 'in_transit']).default('available'),
    is_reference_only: z.boolean().default(false),
    is_rare_book: z.boolean().default(false),
    requires_approval: z.boolean().default(false),
    internal_notes: z.string().optional(),
  })).min(1, 'At least one copy is required'),
});

type AddBookCopyFormData = z.infer<typeof addBookCopySchema>;

interface AddBookCopyFormProps {
  bookId: number;
  bookTitle: string;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const inputCls = "w-full px-3 py-2 border border-[var(--lib-hairline)] rounded-lg text-sm text-[var(--lib-ink)] placeholder:text-[var(--lib-ink-muted)] focus:outline-none focus:border-[var(--lib-gold)] focus:ring-2 focus:ring-[var(--lib-gold)]/15 bg-transparent";
const labelCls = "block font-mono text-[10px] uppercase tracking-wider text-[var(--lib-ink-muted)] mb-1.5";

export const AddBookCopyForm: React.FC<AddBookCopyFormProps> = ({
  bookId,
  bookTitle,
  open,
  onClose,
  onSuccess,
}) => {
  const { toast } = useToast();
  const createBookCopyMutation = useCreateBookCopy();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch reference data
  const { data: locationsData } = useShelfLocations({ page_size: 100 });
  const { data: conditionsData } = useConditions({ page_size: 100 });

  const locations = locationsData?.results || [];
  const conditions = conditionsData?.results || [];

  // Get default condition (usually "New" or the highest score)
  const defaultCondition = conditions.length > 0
    ? conditions.reduce((max, cond) => cond.score > max.score ? cond : max, conditions[0])
    : null;

  const buildDefaultCopy = () => ({
    barcode: generateCopyBarcode(),
    accession_number: '',
    acquisition_date: new Date().toISOString().split('T')[0],
    acquisition_cost: '',
    acquisition_source: '',
    location_id: null,
    condition_id: defaultCondition?.id || 1,
    status: 'available' as const,
    is_reference_only: false,
    is_rare_book: false,
    requires_approval: false,
    internal_notes: '',
  });

  const buildDefaultValues = () => ({
    copies: [buildDefaultCopy()],
  });

  const form = useForm<AddBookCopyFormData>({
    resolver: zodResolver(addBookCopySchema),
    defaultValues: buildDefaultValues(),
  });

  useEffect(() => {
    if (open) {
      form.reset(buildDefaultValues());
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const addCopy = () => {
    const currentCopies = form.getValues('copies');
    form.setValue('copies', [
      ...currentCopies,
      buildDefaultCopy(),
    ]);
  };

  const regenerateBarcode = (index: number) => {
    form.setValue(`copies.${index}.barcode`, generateCopyBarcode(), {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const removeCopy = (index: number) => {
    const currentCopies = form.getValues('copies');
    if (currentCopies.length > 1) {
      form.setValue('copies', currentCopies.filter((_, i) => i !== index));
    }
  };

  const onSubmit = async (data: AddBookCopyFormData) => {
    setIsSubmitting(true);
    let successCount = 0;
    let errorCount = 0;

    try {
      for (const copyData of data.copies) {
        try {
          const createPayload: CreateBookCopyData = {
            book_id: bookId,
            barcode: copyData.barcode,
            accession_number: copyData.accession_number,
            acquisition_date: copyData.acquisition_date,
            status: copyData.status,
            condition_id: copyData.condition_id,
            is_reference_only: copyData.is_reference_only,
            is_rare_book: copyData.is_rare_book,
            requires_approval: copyData.requires_approval,
          };

          if (copyData.acquisition_cost) {
            createPayload.acquisition_cost = copyData.acquisition_cost;
          }
          if (copyData.acquisition_source) {
            createPayload.acquisition_source = copyData.acquisition_source;
          }
          if (copyData.location_id) {
            createPayload.location_id = copyData.location_id;
          }
          if (copyData.internal_notes) {
            createPayload.internal_notes = copyData.internal_notes;
          }

          await createBookCopyMutation.mutateAsync(createPayload);
          successCount++;
        } catch (error) {
          console.error('Error creating copy:', error);
          errorCount++;
        }
      }

      if (successCount > 0) {
        toast({
          title: 'Success',
          description: `Successfully added ${successCount} cop${successCount === 1 ? 'y' : 'ies'} to inventory${errorCount > 0 ? ` (${errorCount} failed)` : ''}`,
        });
        onSuccess();
        onClose();
        form.reset(buildDefaultValues());
      } else {
        throw new Error('All copies failed to create');
      }
    } catch (error) {
      console.error('Error adding book copies:', error);
      toast({
        title: 'Error',
        description: 'Failed to add book copies to inventory',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const statusOptions = [
    { value: 'available', label: 'Available for borrowing' },
    { value: 'maintenance', label: 'Under maintenance/repair' },
    { value: 'in_transit', label: 'Being processed/catalogued' },
  ];

  const copiesCount = form.watch('copies')?.length || 0;
  const isPending = isSubmitting || createBookCopyMutation.isPending;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={`Add Copies — ${bookTitle}`}
      width={560}
      footer={
        <div className="flex gap-2 justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-[var(--lib-ink-muted)] border border-[var(--lib-hairline)] rounded-lg hover:bg-[var(--lib-surface-2)] transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={form.handleSubmit(onSubmit)}
            disabled={isPending}
            className="bg-[var(--lib-gold)] hover:brightness-105 disabled:opacity-50 text-[var(--lib-on-gold)] font-medium text-sm rounded-lg px-4 py-2 transition-colors flex items-center gap-2"
          >
            {isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Add {copiesCount} Cop{copiesCount === 1 ? 'y' : 'ies'}
          </button>
        </div>
      }
    >
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          {form.watch('copies')?.map((copy, index) => (
            <div key={index} className="p-4 border border-[var(--lib-hairline)] rounded-lg space-y-4 bg-[var(--lib-surface-2)]">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold text-[var(--lib-ink)]">Copy #{index + 1}</h4>
                {form.watch('copies').length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeCopy(index)}
                    className="p-1 rounded text-[var(--lib-ink-muted)] hover:text-[var(--lib-danger)] hover:bg-[var(--lib-surface-2)] transition-colors"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 gap-4">
                <FormField
                  control={form.control}
                  name={`copies.${index}.barcode`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className={labelCls}>Barcode *</FormLabel>
                      <FormControl>
                        <div className="flex gap-2">
                          <Input
                            placeholder="12-digit internal barcode"
                            inputMode="numeric"
                            maxLength={12}
                            {...field}
                            onChange={(event) => field.onChange(sanitizeCopyBarcode(event.target.value))}
                            className={inputCls}
                          />
                          <button
                            type="button"
                            onClick={() => regenerateBarcode(index)}
                            className="px-2.5 py-1.5 text-xs border border-[var(--lib-hairline)] rounded-lg text-[var(--lib-ink-muted)] hover:bg-[var(--lib-surface-2)] transition-colors"
                            title="Generate new barcode"
                          >
                            <RefreshCw className="h-4 w-4" />
                          </button>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name={`copies.${index}.accession_number`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className={labelCls}>Accession Number *</FormLabel>
                      <FormControl>
                        <Input placeholder="e.g., ACC-2025-001" {...field} className={inputCls} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name={`copies.${index}.acquisition_date`}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className={labelCls}>Acquisition Date *</FormLabel>
                        <FormControl>
                          <Input type="date" {...field} className={inputCls} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name={`copies.${index}.acquisition_cost`}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className={labelCls}>Cost (Optional)</FormLabel>
                        <FormControl>
                          <Input type="number" step="0.01" placeholder="0.00" {...field} className={inputCls} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name={`copies.${index}.acquisition_source`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className={labelCls}>Source (Optional)</FormLabel>
                      <FormControl>
                        <Input placeholder="e.g., Amazon, Donation" {...field} className={inputCls} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name={`copies.${index}.location_id`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className={labelCls}>Shelf Location</FormLabel>
                      <Select
                        onValueChange={(value) => {
                          if (value === 'none') {
                            field.onChange(null);
                          } else {
                            field.onChange(parseInt(value, 10));
                          }
                        }}
                        value={field.value !== null && field.value !== undefined ? field.value.toString() : 'none'}
                      >
                        <FormControl>
                          <SelectTrigger className={inputCls}>
                            <SelectValue placeholder="Select location" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="none">Not assigned yet</SelectItem>
                          {locations.map((location) => (
                            <SelectItem key={location.id} value={location.id.toString()}>
                              {location.label} · {location.library}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name={`copies.${index}.condition_id`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className={labelCls}>Condition *</FormLabel>
                      <Select
                        onValueChange={(value) => field.onChange(parseInt(value, 10))}
                        value={field.value?.toString()}
                      >
                        <FormControl>
                          <SelectTrigger className={inputCls}>
                            <SelectValue placeholder="Select condition" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {conditions.map((condition) => (
                            <SelectItem key={condition.id} value={condition.id.toString()}>
                              {condition.name} (Score: {condition.score})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name={`copies.${index}.status`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className={labelCls}>Initial Status *</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger className={inputCls}>
                            <SelectValue placeholder="Select status" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {statusOptions.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="space-y-3 pt-2 border-t border-[var(--lib-hairline)]">
                <FormField
                  control={form.control}
                  name={`copies.${index}.is_reference_only`}
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                      <FormControl>
                        <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                      <div className="space-y-0.5 leading-none">
                        <FormLabel className="text-sm font-medium text-[var(--lib-ink)]">Reference Only</FormLabel>
                        <FormDescription className="text-xs text-[var(--lib-ink-muted)]">
                          Cannot be borrowed, must be used in library
                        </FormDescription>
                      </div>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name={`copies.${index}.is_rare_book`}
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                      <FormControl>
                        <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                      <div className="space-y-0.5 leading-none">
                        <FormLabel className="text-sm font-medium text-[var(--lib-ink)]">Rare/Valuable Book</FormLabel>
                        <FormDescription className="text-xs text-[var(--lib-ink-muted)]">
                          Special handling and security required
                        </FormDescription>
                      </div>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name={`copies.${index}.requires_approval`}
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                      <FormControl>
                        <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                      <div className="space-y-0.5 leading-none">
                        <FormLabel className="text-sm font-medium text-[var(--lib-ink)]">Requires Approval</FormLabel>
                        <FormDescription className="text-xs text-[var(--lib-ink-muted)]">
                          Librarian approval needed before borrowing
                        </FormDescription>
                      </div>
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name={`copies.${index}.internal_notes`}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className={labelCls}>Internal Notes</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Any special notes about this copy..."
                        {...field}
                        className={inputCls}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          ))}

          <button
            type="button"
            onClick={addCopy}
            className="w-full flex items-center justify-center gap-2 py-2.5 text-sm text-[var(--lib-ink-muted)] border border-dashed border-[var(--lib-hairline)] rounded-lg hover:bg-[var(--lib-surface-2)] hover:border-[var(--lib-ink-muted)] transition-colors"
          >
            <Plus className="h-4 w-4" />
            Add Another Copy
          </button>
        </form>
      </Form>
    </Drawer>
  );
};

export default AddBookCopyForm;
